import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddOutlined,
  DeleteOutline,
  EditOutlined,
  RefreshOutlined,
  SearchOutlined,
  AdminPanelSettingsOutlined,
  PersonOutlineOutlined,
  FilterAltOutlined,
  ClearOutlined,
  CampaignOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import AddAdminDialog from "./AddAdminDialog";
import EditAdminDialog from "./EditAdminDialog";
import ApiConfig from "../../Config";
import adminService from "../../repository/adminRepository";

const normalizeRole = (role) => {
  const value = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  if (value === "SUPERADMIN") {
    return "SUPER_ADMIN";
  }

  return value;
};

const AdminUser = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const accentColor = isDark ? "#D1B99A" : "#806B52";

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editStaff, setEditStaff] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteStaff, setDeleteStaff] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const currentRole = normalizeRole(sessionStorage.getItem("role"));

  const canManage = ["SUPER_ADMIN", "ADMIN"].includes(currentRole);

  const token = sessionStorage.getItem("newToken");

  const api = useMemo(
    () =>
      axios.create({
        baseURL: ApiConfig.apiBaseUrl,
        headers: {
          "x-access-token": token,
        },
      }),
    [token],
  );

  const fetchStaff = async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      //   const response = await api.get(ApiConfig.getAdminUsers);
      const response = await adminService.getAdminUsers(token);

      const users =
        response.data?.adminUsers ||
        response.data?.data ||
        response.data?.users ||
        [];

      const fetchedStaff = Array.isArray(users)
        ? users.map((user) => ({
            id: user._id,
            name: user.name || "N/A",
            employeeId: user.adminId || "N/A",
            email: user.email || "N/A",
            role: normalizeRole(user.role || "STANDARD"),
            createdBy: user.createdBy || "N/A",
            dob: user.admin_DOB || "N/A",
            contact: user.contact || "",
            address: {
              district: user.address?.district || "",
              city: user.address?.city || "",
              state: user.address?.state || "",
              country: user.address?.country || "",
              pinCode: user.address?.pinCode || "",
            },
          }))
        : [];

      setStaff(fetchedStaff);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to fetch admin users");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (canManage) {
      fetchStaff();
    } else {
      setLoading(false);
    }
  }, [canManage]);

  const filteredStaff = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return staff.filter((member) => {
      const matchesSearch =
        !search ||
        String(member.name || "")
          .toLowerCase()
          .includes(search) ||
        String(member.email || "")
          .toLowerCase()
          .includes(search) ||
        String(member.employeeId || "")
          .toLowerCase()
          .includes(search) ||
        String(member.role || "")
          .toLowerCase()
          .includes(search);

      const matchesRole =
        roleFilter === "all" || normalizeRole(member.role) === roleFilter;

      return matchesSearch && matchesRole;
    });
  }, [staff, searchTerm, roleFilter]);

  const statistics = useMemo(
    () => ({
      total: staff.length,

      superAdmin: staff.filter(
        (item) => normalizeRole(item.role) === "SUPER_ADMIN",
      ).length,

      admin: staff.filter((item) => normalizeRole(item.role) === "ADMIN")
        .length,

      standard: staff.filter((item) => normalizeRole(item.role) === "STANDARD")
        .length,

      marketing: staff.filter(
        (item) => normalizeRole(item.role) === "MARKETING",
      ).length,
    }),
    [staff],
  );

  const getRoleConfig = (role) => {
    const normalized = normalizeRole(role);

    if (normalized === "SUPER_ADMIN") {
      return {
        label: "Super Admin",
        color: isDark ? "#D1B99A" : "#806B52",
        background: isDark ? "rgba(209,185,154,0.12)" : "rgba(128,107,82,0.10)",
        border: isDark ? "rgba(209,185,154,0.28)" : "rgba(128,107,82,0.22)",
        icon: <ShieldOutlined />,
      };
    }

    if (normalized === "ADMIN") {
      return {
        label: "Admin",
        color: "#ed6c02",
        background: "rgba(237,108,2,0.10)",
        border: "rgba(237,108,2,0.22)",
        icon: <AdminPanelSettingsOutlined />,
      };
    }

    if (normalized === "MARKETING") {
      return {
        label: "Marketing",
        color: "#7b1fa2",
        background: "rgba(123,31,162,0.10)",
        border: "rgba(123,31,162,0.22)",
        icon: <CampaignOutlined />,
      };
    }

    return {
      label: "Standard",
      color: "#2e7d32",
      background: "rgba(46,125,50,0.10)",
      border: "rgba(46,125,50,0.22)",
      icon: <PersonOutlineOutlined />,
    };
  };

  const handleEditStaff = (member) => {
    setEditStaff(member);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (member) => {
    setDeleteStaff(member);
    setDeleteDialogOpen(true);
  };

  const handleDeleteStaff = async () => {
    if (!deleteStaff?.employeeId) {
      return;
    }

    try {
      setDeleting(true);

      await api.delete(ApiConfig.deleteAdmin, {
        headers: {
          "x-access-token": token,
          "admin-id": deleteStaff.employeeId,
        },
      });

      setSuccess("Admin user deleted successfully");

      setDeleteDialogOpen(false);

      setDeleteStaff(null);

      await fetchStaff();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete admin user");
    } finally {
      setDeleting(false);
    }
  };

  const handleAddStaffSuccess = async () => {
    setAddDialogOpen(false);

    setSuccess("Admin user created successfully");

    await fetchStaff();
  };

  const handleEditStaffSuccess = async () => {
    setEditDialogOpen(false);

    setEditStaff(null);

    setSuccess("Admin user updated successfully");

    await fetchStaff();
  };

  const clearFilters = () => {
    setSearchTerm("");
    setRoleFilter("all");
  };

  const closeSnackbar = () => {
    setSuccess("");
    setError("");
  };

  if (!canManage) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 70px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          backgroundColor: "background.default",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 480,
            p: 5,
            textAlign: "center",
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            boxShadow: isDark
              ? "0 24px 70px rgba(0,0,0,.3)"
              : "0 24px 70px rgba(65,45,25,.08)",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              mx: "auto",
              mb: 2,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isDark
                ? "rgba(209,185,154,0.12)"
                : "rgba(128,107,82,0.10)",
              color: accentColor,
            }}
          >
            <ShieldOutlined
              sx={{
                fontSize: 36,
              }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: 23,
              fontWeight: 750,
              mb: 0.7,
              letterSpacing: "-0.4px",
            }}
          >
            Access Restricted
          </Typography>

          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
              lineHeight: 1.7,
            }}
          >
            You do not have permission to manage admin users.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 70px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "background.default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress
            size={34}
            sx={{
              color: accentColor,
            }}
          />

          <Typography variant="body2" color="text.secondary">
            Loading admin users...
          </Typography>
        </Box>
      </Box>
    );
  }

  const statisticsCards = [
    {
      label: "Total Users",
      value: statistics.total,
      icon: <PersonOutlineOutlined />,
      color: accentColor,
      background: isDark ? "rgba(209,185,154,0.12)" : "rgba(128,107,82,0.10)",
    },
    {
      label: "Super Admin",
      value: statistics.superAdmin,
      icon: <ShieldOutlined />,
      color: accentColor,
      background: isDark ? "rgba(209,185,154,0.12)" : "rgba(128,107,82,0.10)",
    },
    {
      label: "Admin",
      value: statistics.admin,
      icon: <AdminPanelSettingsOutlined />,
      color: "#ed6c02",
      background: "rgba(237,108,2,0.10)",
    },
    {
      label: "Standard",
      value: statistics.standard,
      icon: <PersonOutlineOutlined />,
      color: "#2e7d32",
      background: "rgba(46,125,50,0.10)",
    },
    {
      label: "Marketing",
      value: statistics.marketing,
      icon: <CampaignOutlined />,
      color: "#7b1fa2",
      background: "rgba(123,31,162,0.10)",
    },
  ];

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          sm: 2.5,
          md: 3,
        },
        minHeight: "calc(100vh - 70px)",
        backgroundColor: "background.default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
          justifyContent: "space-between",
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              mb: 0.5,
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isDark
                  ? "rgba(209,185,154,0.12)"
                  : "rgba(128,107,82,0.10)",
                color: accentColor,
              }}
            >
              <AdminPanelSettingsOutlined />
            </Box>

            <Typography
              sx={{
                fontSize: {
                  xs: 22,
                  md: 27,
                },
                fontWeight: 750,
                letterSpacing: "-0.5px",
              }}
            >
              Admin Management
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: 13,
              color: "text.secondary",
              ml: {
                xs: 0,
                md: 6.5,
              },
            }}
          >
            Manage administrators, roles and access permissions.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: {
              xs: "100%",
              md: "auto",
            },
          }}
        >
          <Tooltip title="Refresh admins">
            <IconButton
              onClick={() => fetchStaff(true)}
              disabled={refreshing}
              sx={{
                width: 42,
                height: 42,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                color: "text.secondary",
                backgroundColor: "background.paper",

                "&:hover": {
                  backgroundColor: "action.hover",
                  borderColor: accentColor,
                  color: accentColor,
                },
              }}
            >
              {refreshing ? (
                <CircularProgress size={19} />
              ) : (
                <RefreshOutlined
                  sx={{
                    fontSize: 21,
                  }}
                />
              )}
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              height: 42,
              px: 2.2,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 13,
              fontWeight: 700,
              backgroundColor: accentColor,
              color: isDark ? "#211F1C" : "#fff",
              boxShadow: "none",

              "&:hover": {
                backgroundColor: isDark ? "#E0CDB2" : "#6F5C46",
                boxShadow: "none",
              },
            }}
          >
            Add Admin User
          </Button>
        </Box>
      </Box>

      <Grid
        container
        spacing={2}
        sx={{
          mb: 3,
        }}
      >
        {statisticsCards.map((item) => (
          <Grid item xs={6} sm={4} md={2.4} key={item.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2.2,
                height: "100%",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                backgroundColor: "background.paper",
                transition: "all .2s ease",

                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: isDark
                    ? "0 12px 30px rgba(0,0,0,.18)"
                    : "0 12px 30px rgba(65,45,25,.07)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      color: "text.secondary",
                      mb: 0.7,
                      fontWeight: 600,
                    }}
                  >
                    {item.label}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 26,
                      fontWeight: 750,
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: item.background,
                    color: item.color,
                  }}
                >
                  {item.icon}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          backgroundColor: "background.paper",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: {
              xs: 1.5,
              md: 2,
            },
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  height: 44,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  backgroundColor: "action.hover",
                  transition: "all .2s ease",

                  "&:focus-within": {
                    borderColor: accentColor,
                    backgroundColor: "background.paper",
                    boxShadow: isDark
                      ? "0 0 0 3px rgba(209,185,154,.08)"
                      : "0 0 0 3px rgba(128,107,82,.08)",
                  },
                }}
              >
                <SearchOutlined
                  sx={{
                    ml: 1.5,
                    color: "text.secondary",
                    fontSize: 20,
                  }}
                />

                <InputBase
                  fullWidth
                  placeholder="Search name, email, admin ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    ml: 1,
                    mr: 1,
                    flex: 1,
                    fontSize: 13,
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Select
                fullWidth
                size="small"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                sx={{
                  height: 44,
                  borderRadius: 2,
                  fontSize: 13,
                  backgroundColor: "action.hover",
                }}
                startAdornment={
                  <FilterAltOutlined
                    sx={{
                      ml: 1,
                      mr: 0.5,
                      fontSize: 18,
                      color: "text.secondary",
                    }}
                  />
                }
              >
                <MenuItem value="all">All Roles</MenuItem>

                <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>

                <MenuItem value="ADMIN">Admin</MenuItem>

                <MenuItem value="STANDARD">Standard</MenuItem>

                <MenuItem value="MARKETING">Marketing</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearOutlined />}
                onClick={clearFilters}
                sx={{
                  height: 44,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 13,
                  borderColor: "divider",
                  color: "text.secondary",
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              Admin Users
            </Typography>

            <Typography
              sx={{
                fontSize: 11.5,
                color: "text.secondary",
                mt: 0.2,
              }}
            >
              Showing {filteredStaff.length} of {staff.length} users
            </Typography>
          </Box>

          {(searchTerm || roleFilter !== "all") && (
            <Chip
              size="small"
              label="Filters active"
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                fontSize: 11,
              }}
            />
          )}
        </Box>

        <TableContainer
          sx={{
            maxHeight: {
              xs: "calc(100vh - 390px)",
              md: "calc(100vh - 360px)",
            },
            overflowX: "auto",
            overflowY: "auto",
          }}
        >
          <Table
            stickyHeader
            sx={{
              minWidth: 1050,
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  "Admin ID",
                  "Name",
                  "Email",
                  "Role",
                  "Date of Birth",
                  "Created By",
                  "Actions",
                ].map((heading) => (
                  <TableCell
                    key={heading}
                    sx={{
                      backgroundColor: isDark ? "#211F1C" : "#F6F2ED",
                      color: "text.secondary",
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      whiteSpace: "nowrap",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      py: 1.7,
                    }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredStaff.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{
                      borderBottom: 0,
                      py: 9,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 58,
                          height: 58,
                          borderRadius: 2.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "action.hover",
                          mb: 1.5,
                        }}
                      >
                        <AdminPanelSettingsOutlined
                          sx={{
                            color: "text.secondary",
                            fontSize: 27,
                          }}
                        />
                      </Box>

                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 650,
                        }}
                      >
                        No admin users found
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "text.secondary",
                          mt: 0.5,
                        }}
                      >
                        Try changing your search or role filter.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStaff.map((member) => {
                  const role = getRoleConfig(member.role);

                  return (
                    <TableRow
                      key={member.id}
                      hover
                      sx={{
                        "&:last-child td": {
                          borderBottom: 0,
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          borderColor: "divider",
                        }}
                      >
                        {member.employeeId}
                      </TableCell>

                      <TableCell
                        sx={{
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 650,
                          }}
                        >
                          {member.name}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 12.5,
                          borderColor: "divider",
                        }}
                      >
                        <Tooltip title={member.email}>
                          <Typography
                            noWrap
                            sx={{
                              maxWidth: 220,
                              fontSize: 12.5,
                            }}
                          >
                            {member.email}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell
                        sx={{
                          borderColor: "divider",
                        }}
                      >
                        <Chip
                          icon={role.icon}
                          label={role.label}
                          size="small"
                          sx={{
                            height: 28,
                            borderRadius: 1.5,
                            fontSize: 11,
                            fontWeight: 650,
                            color: role.color,
                            backgroundColor: role.background,
                            border: `1px solid ${role.border}`,
                            "& .MuiChip-icon": {
                              color: role.color,
                              fontSize: 15,
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 12.5,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          borderColor: "divider",
                        }}
                      >
                        {member.dob}
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 12.5,
                          color: "text.secondary",
                          borderColor: "divider",
                        }}
                      >
                        {member.createdBy}
                      </TableCell>

                      <TableCell
                        sx={{
                          borderColor: "divider",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title="Edit admin">
                            <IconButton
                              size="small"
                              onClick={() => handleEditStaff(member)}
                              sx={{
                                width: 34,
                                height: 34,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1.5,
                                color: "text.secondary",

                                "&:hover": {
                                  color: accentColor,
                                  borderColor: accentColor,
                                  backgroundColor: isDark
                                    ? "rgba(209,185,154,0.08)"
                                    : "rgba(128,107,82,0.06)",
                                },
                              }}
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete admin">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(member)}
                              sx={{
                                width: 34,
                                height: 34,
                                border: "1px solid",
                                borderColor: "rgba(211,47,47,0.20)",
                                borderRadius: 1.5,
                                color: "#d32f2f",

                                "&:hover": {
                                  backgroundColor: "rgba(211,47,47,0.08)",
                                  borderColor: "rgba(211,47,47,0.35)",
                                },
                              }}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            px: 2,
            py: 1.4,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            sx={{
              fontSize: 11.5,
              color: "text.secondary",
            }}
          >
            Showing {filteredStaff.length} of {staff.length} admin users
          </Typography>
        </Box>
      </Paper>

      <AddAdminDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onStaffAdded={handleAddStaffSuccess}
      />

      <EditAdminDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditStaff(null);
        }}
        staffData={editStaff}
        onSave={handleEditStaffSuccess}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          Delete Admin User?
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{
              fontSize: 13,
              lineHeight: 1.7,
            }}
          >
            Are you sure you want to delete <strong>{deleteStaff?.name}</strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            pt: 1,
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleDeleteStaff}
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutline />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              backgroundColor: "#d32f2f",
              boxShadow: "none",

              "&:hover": {
                backgroundColor: "#b71c1c",
                boxShadow: "none",
              },
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={closeSnackbar}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
          }}
        >
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={closeSnackbar}
          severity="error"
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUser;
